import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  Button,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  AvatarGroup
} from '@mui/material';
import { 
  MoreVert as MoreVertIcon, 
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  TodayOutlined as TodayIcon,
  CalendarToday as CalendarIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProjectDetailsDialogEmp from './ViewProjectEmp';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AllProjectsEmployee = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [starredProjects, setStarredProjects] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuProject, setMenuProject] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
     const [employeeId, setEmployeeId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Helper function to get consistent avatar color based on project key
  const getAvatarColor = (key) => {
    const colors = [
      '#2196F3', // Blue
      '#4CAF50', // Green
      '#FF9800', // Orange
      '#E91E63', // Pink
      '#9C27B0', // Purple
      '#00BCD4', // Cyan
      '#F44336', // Red
      '#3F51B5'  // Indigo
    ];
    
    let hash = 0;
    if (!key) return colors[0];
    
    // Simple hash function to get consistent color
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };
    const getEmployeeid = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });

      if (!response.data || !response.data.employee_id) {
        throw new Error('Employee ID not found in user data');
      }

      setEmployeeId(response.data.employee_id);
      return response.data.employee_id;
    } catch (err) {
      setError('Failed to fetch user information');
      return null;
    }
  };
  // Helper function to calculate progress - FIXED VERSION
  const calculateProgress = (project) => {
    // Priority 1: Use the completionPercentage if it exists and is valid
    if (project.completionPercentage !== undefined && 
        project.completionPercentage !== null && 
        !isNaN(project.completionPercentage)) {
      return Math.max(0, Math.min(100, parseInt(project.completionPercentage)));
    }
    
    // Priority 2: Calculate from tasks if available
    if (project.tasks && Array.isArray(project.tasks) && project.tasks.length > 0) {
      const completedTasks = project.tasks.filter(task => 
        task.status === 'Completed' || task.status === 'Done'
      ).length;
      return Math.floor((completedTasks / project.tasks.length) * 100);
    }
    
    // Priority 3: Calculate based on project status
    switch (project.status) {
      case 'Completed':
        return 100;
      case 'Active':
      case 'In Progress':
        // For active projects, calculate based on time elapsed if dates are available
        if (project.start_date && project.end_date) {
          const startDate = new Date(project.start_date);
          const endDate = new Date(project.end_date);
          const currentDate = new Date();
          
          if (currentDate < startDate) return 0;
          if (currentDate > endDate) return 90; // Almost complete but not 100% unless explicitly completed
          
          const totalDuration = endDate - startDate;
          const elapsed = currentDate - startDate;
          
          return Math.min(90, Math.floor((elapsed / totalDuration) * 100));
        }
        return 25; // Default for active projects
      case 'Planning':
        return 10;
      case 'On Hold':
        return 15;
      case 'Not Started':
      default:
        return 0;
    }
  };

  // Helper function to determine priority
  const determinePriority = (project) => {
    if (project.priority) return project.priority;
    
    // Assign priorities based on project attributes
    if (project.end_date && new Date(project.end_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) return 'High';
    if (project.status === 'Active') return 'Medium';
    
    const priorities = ['High', 'Medium', 'Low'];
    const hash = project.project_id ? parseInt(project.project_id, 10) : 0;
    return priorities[hash % 3];
  };
  
  // Fetch projects from the backend
  useEffect(() => {
    const fetchProjects = async () => {
      try {
            const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
              const employee_id = await getEmployeeid();

        setLoading(true);
        const response = await axios.get(`${API_URL}/api/projects/active/active-project-employee?employee_id=${employee_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.status === "success") {
          setProjects(response.data.data);
          
          // Load starred projects from local storage
          const storedStarred = localStorage.getItem('starredProjects');
          if (storedStarred) {
            setStarredProjects(new Set(JSON.parse(storedStarred)));
          }
        } else {
          setError('Failed to load projects');
        }
      } catch (err) {
        setError(err.message || 'An error occurred while fetching projects');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);
  
  // Format project data for display - UPDATED
  const formatProjects = (projectsData) => {
    return projectsData.map(project => {
      const progress = calculateProgress(project);
      const priority = determinePriority(project);
      
      return {
        id: project.project_id,
        name: project.name,
        key: project.key,
        type: project.projectType,
        description: project.description,
        lead: project.projectLead ? project.projectLead.name : 'Unassigned',
        leadAvatar: project.projectLead?.avatar || null,
        projectManagers: project.projectManagers ? project.projectManagers.map(pm => pm.name).join(', ') : '',
        starred: starredProjects.has(project.project_id),
        status: project.status || 'Not Started',
        lastUpdated: formatDate(project.updatedAt || project.createdAt),
        teamMembers: project.teamMembers || [],
        startDate: project.start_date,
        endDate: project.end_date,
        budget: project.budget,
        product_manager_id: project.product_manager_id,
        product_owner_id: project.product_owner_id,
        avatar_bg: getAvatarColor(project.key),
        progress: progress, // This is the calculated progress
        completionPercentage: progress, // Make sure both properties have the same value
        priority: priority,
        tasks: {
          total: project.tasks?.length || 0,
          completed: project.tasks?.filter(task => 
            task.status === 'Completed' || task.status === 'Done'
          ).length || 0
        },
        team: project.teamMembers?.map(member => ({
          id: member.employee_id || member.id,
          name: member.name,
          avatar: member.avatar || null
        })) || []
      };
    });
  };
  
  // Format date as "X days/hours ago"
  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    }
  };
  
  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Handle project menu
  const handleMenuOpen = (event, project) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuProject(project);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuProject(null);
  };
  
  // Handle filter menu
  const handleFilterMenuOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterMenuClose = (type = null) => {
    if (type) {
      setFilterType(type);
    }
    setFilterAnchorEl(null);
  };
  
  // Toggle star for a project
  const toggleStar = (id) => {
    const newStarred = new Set(starredProjects);
    if (newStarred.has(id)) {
      newStarred.delete(id);
    } else {
      newStarred.add(id);
    }
    setStarredProjects(newStarred);
    
    // Save to local storage
    localStorage.setItem('starredProjects', JSON.stringify([...newStarred]));
  };
  
  // Handle search
  const handleSearch = (event) => {
    setSearchText(event.target.value);
  };
  
  // Handle viewing project details
  const handleViewProject = (projectId) => {
    setSelectedProjectId(projectId);
    setDialogOpen(true);
  };

  // Handler for closing project details dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  // Navigate to edit project page
  const handleEditProject = (project) => {
    // Store project data in localStorage to pass to the edit form
    localStorage.setItem('editProject', JSON.stringify(project));
    navigate(`/create-project?edit=true&id=${project.id}`);
  };
  
  // Delete a project
  const handleDeleteProject = async (id) => {
    try {
          const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.delete(`${API_URL}/api/projects/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.status === "success") {
        setProjects(projects.filter(project => project.project_id !== id));
        setSnackbar({
          open: true,
          message: 'Project deleted successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to delete project',
          severity: 'error'
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'An error occurred while deleting project',
        severity: 'error'
      });
    }
    handleMenuClose();
  };
  
  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Apply filters and sorting
  const applyFiltersAndSort = () => {
    let filteredProjects = [...formatProjects(projects)];
    
    // Apply search filter
    if (searchText) {
      filteredProjects = filteredProjects.filter(project => 
        project.name.toLowerCase().includes(searchText.toLowerCase()) ||
        project.key.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    // Apply category filter
    switch (filterType) {
      case 'starred':
        filteredProjects = filteredProjects.filter(project => project.starred);
        break;
      case 'active':
        filteredProjects = filteredProjects.filter(project => project.status === 'Active');
        break;
      case 'completed':
        filteredProjects = filteredProjects.filter(project => project.status === 'Completed');
        break;
      case 'planning':
        filteredProjects = filteredProjects.filter(project => project.status === 'Planning');
        break;
      case 'on-hold':
        filteredProjects = filteredProjects.filter(project => project.status === 'On Hold');
        break;
      default:
        // 'all' - no filtering
        break;
    }
    
    // Sort by name by default
    filteredProjects.sort((a, b) => a.name.localeCompare(b.name));
    
    return filteredProjects;
  };
  
  const filteredProjects = applyFiltersAndSort();
  
  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Active': return 'primary';
      case 'Planning': return 'info';
      case 'Completed': return 'success';
      case 'On Hold': return 'warning';
      case 'Not Started': return 'default';
      default: return 'default';
    }
  };

  // Handle project selection for navigation
  const handleProjectSelect = (projectId) => {
    navigate(`/project/${projectId}/roadmap`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" fontWeight='bold'>All Projects</Typography>
        {/* <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => navigate('/create-project')}
        >
          Create Project
        </Button> */}
      </Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <TextField
            placeholder="Search projects"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearch}
            sx={{ width: '400px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Box>
            <Button 
              variant="outlined" 
              startIcon={<FilterListIcon />}
              onClick={handleFilterMenuOpen}
              sx={{ mr: 1 }}
            >
              Filter
            </Button>
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={() => handleFilterMenuClose()}
            >
              <MenuItem onClick={() => handleFilterMenuClose('all')}>All Projects</MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('starred')}>Starred</MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('active')}>Active</MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('planning')}>Planning</MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('completed')}>Completed</MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('on-hold')}>On Hold</MenuItem>
            </Menu>
          </Box>
        </Box>
        
        <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
          Showing {filteredProjects.length} of {projects.length} projects
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          <Grid container spacing={3}>
            {filteredProjects.map(project => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <Card 
                  sx={{ 
                    height: '90%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: project.avatar_bg,
                            width: 36, 
                            height: 36,
                            mr: 1.5,
                            fontSize: '0.875rem'
                          }}
                        >
                          {project.key}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
                            {project.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {project.type}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStar(project.id);
                          }}
                          color={project.starred ? "warning" : "default"}
                        >
                          {project.starred ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleMenuOpen(e, project)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Status and Priority Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Chip 
                        label={project.status} 
                        size="small"
                        color={getStatusColor(project.status)}
                        sx={{ height: 24 }}
                      />
                      <Chip 
                        label={project.priority} 
                        size="small"
                        color={
                          project.priority === 'High' ? 'error' : 
                          project.priority === 'Medium' ? 'warning' : 'success'
                        }
                        variant="outlined"
                        sx={{ height: 24 }}
                      />
                    </Box>

                    {/* Progress Bar - FIXED SECTION */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          Progress
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {project.progress}%
                        </Typography>
                      </Box>
                      <Box sx={{
                        width: '100%',
                        height: 8,
                        bgcolor: '#f0f0f0',
                        borderRadius: 4
                      }}>
                        <Box
                          sx={{
                            width: `${project.progress}%`,
                            height: '100%',
                            bgcolor:
                              project.progress < 30 ? '#ff9800' :
                              project.progress < 70 ? '#2196f3' :
                              '#4caf50',
                            borderRadius: 4,
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                      </Box>
                    </Box>


                    {/* Dates Row */}
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 9 }}>
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <CalendarIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
    <Typography variant="body2" color="textSecondary">
      Start: {formatDisplayDate(project.startDate)}
    </Typography>
  </Box>
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <TodayIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
    <Typography variant="body2" color="textSecondary">
      End: {formatDisplayDate(project.endDate)}
    </Typography>
  </Box>
</Box>

                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {filteredProjects.length === 0 && !loading && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              No projects found
            </Typography>
            {/* <Typography variant="body2" color="textSecondary">
              {searchText ? 'Try adjusting your search criteria' : 'Create your first project to get started'}
            </Typography> */}
          </Paper>
        )}
      </Paper>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          if (menuProject) handleViewProject(menuProject.id);
        }}>View details</MenuItem>
      </Menu>
      
      {/* Project Details Dialog */}
      <ProjectDetailsDialogEmp 
        open={dialogOpen}
        handleClose={handleCloseDialog}
        projectId={selectedProjectId}
      />
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllProjectsEmployee;